import React from 'react';
import {motion} from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const {FiDollarSign, FiShoppingCart, FiUsers, FiHome, FiMoreHorizontal, FiCalendar} = FiIcons;

const CalculatorForm = ({formData, setFormData, periodType, setPeriodType}) => {
  const handleInputChange = (field, value) => {
    setFormData(prev => ({...prev, [field]: value}));
  };

  const periodOptions = [
    {value: 'daily', label: 'Daily'},
    {value: 'weekly', label: 'Weekly'},
    {value: 'monthly', label: 'Monthly'}
  ];

  const formFields = [
    {
      key: 'revenue',
      label: `${periodType.charAt(0).toUpperCase() + periodType.slice(1)} Revenue`,
      icon: FiDollarSign,
      placeholder: periodType === 'daily' ? '2500.00' : periodType === 'weekly' ? '17500.00' : '75000.00',
      color: 'from-green-500 to-emerald-500'
    },
    {
      key: 'foodCosts',
      label: 'Food & Beverage Costs',
      icon: FiShoppingCart,
      placeholder: periodType === 'daily' ? '800.00' : periodType === 'weekly' ? '5600.00' : '24000.00',
      color: 'from-orange-500 to-red-500'
    },
    {
      key: 'laborCosts',
      label: 'Labor Costs',
      icon: FiUsers,
      placeholder: periodType === 'daily' ? '600.00' : periodType === 'weekly' ? '4200.00' : '18000.00',
      color: 'from-blue-500 to-indigo-500'
    },
    {
      key: 'rentUtilities',
      label: 'Rent & Utilities',
      icon: FiHome,
      placeholder: periodType === 'daily' ? '200.00' : periodType === 'weekly' ? '1400.00' : '6000.00',
      color: 'from-purple-500 to-pink-500'
    },
    {
      key: 'otherExpenses',
      label: 'Other Expenses',
      icon: FiMoreHorizontal,
      placeholder: periodType === 'daily' ? '150.00' : periodType === 'weekly' ? '1050.00' : '4500.00',
      color: 'from-slate-500 to-gray-500'
    }
  ];

  return (
    <motion.div
      initial={{opacity: 0, x: -20}}
      animate={{opacity: 1, x: 0}}
      transition={{duration: 0.6, delay: 0.2}}
      className="bg-white rounded-3xl shadow-xl p-8 border border-slate-200"
    >
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-3">
          <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-2 rounded-xl">
            <SafeIcon icon={FiCalendar} className="text-xl text-white" />
          </div>
          <h3 className="text-xl md:text-2xl font-bold text-slate-800">Financial Calculator</h3>
        </div>
        <div className="flex gap-2 w-full md:w-auto">
          {periodOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => setPeriodType(option.value)}
              className={`flex-1 md:flex-none px-4 py-2 rounded-lg text-sm font-medium transition-all ${
                periodType === option.value
                  ? 'bg-blue-600 text-white shadow-md'
                  : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      <div className="space-y-6">
        {formFields.map((field, index) => (
          <motion.div
            key={field.key}
            initial={{opacity: 0, y: 10}}
            animate={{opacity: 1, y: 0}}
            transition={{duration: 0.4, delay: 0.1 * index}}
            className="relative group"
          >
            <label className="block text-sm font-semibold text-slate-700 mb-2">
              {field.label}
            </label>
            <div className="relative">
              <div className={`absolute left-4 top-1/2 transform -translate-y-1/2 bg-gradient-to-r ${field.color} p-2 rounded-lg shadow-sm`}>
                <span className="text-sm text-white font-bold">RM</span>
              </div>
              <input
                type="number"
                step="0.01"
                placeholder={field.placeholder}
                value={formData[field.key]}
                onChange={(e) => handleInputChange(field.key, e.target.value)}
                className="w-full pl-16 pr-4 py-4 text-lg font-medium border-2 border-slate-200 rounded-2xl focus:border-blue-500 focus:ring-4 focus:ring-blue-100 transition-all duration-200 bg-slate-50 focus:bg-white"
              />
              <div className="absolute right-4 top-1/2 transform -translate-y-1/2 text-slate-400 font-medium hidden sm:block">
                MYR
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      <motion.div
        initial={{opacity: 0}}
        animate={{opacity: 1}}
        transition={{delay: 0.8}}
        className="mt-8 p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border border-blue-200"
      >
        <p className="text-sm text-blue-800 leading-relaxed">
          <strong>💡 Tip:</strong> Enter your {periodType} figures. The calculator will automatically compute your profits based on the selected period.
        </p>
      </motion.div>
    </motion.div>
  );
};

export default CalculatorForm;